export type ChecklistInputLimits = {
  titleMaxLength: number
  descriptionMaxLength: number
  completionCriteriaMaxLength: number
  commentMaxLength: number
  searchMaxLength: number
  autoSpawnTitleMaxLength: number
  weightageMax: number
  estimatedHoursMax: number
  actualCostMax: number
  countdownHoursMax: number
  delayHoursMax: number
  autoSpawnYOffsetMax: number
}

export const CHECKLIST_INPUT_HARD_LIMITS: ChecklistInputLimits = {
  titleMaxLength: 120,
  descriptionMaxLength: 2_000,
  completionCriteriaMaxLength: 2_000,
  commentMaxLength: 500,
  searchMaxLength: 120,
  autoSpawnTitleMaxLength: 120,
  weightageMax: 60,
  estimatedHoursMax: 1_000,
  actualCostMax: 1_000_000,
  countdownHoursMax: 720,
  delayHoursMax: 720,
  autoSpawnYOffsetMax: 2_000,
}

export const getChecklistInputLimitDefaults = (ratio: number): ChecklistInputLimits => ({
  titleMaxLength: Math.round(CHECKLIST_INPUT_HARD_LIMITS.titleMaxLength * ratio),
  descriptionMaxLength: Math.round(CHECKLIST_INPUT_HARD_LIMITS.descriptionMaxLength * ratio),
  completionCriteriaMaxLength: Math.round(CHECKLIST_INPUT_HARD_LIMITS.completionCriteriaMaxLength * ratio),
  commentMaxLength: Math.round(CHECKLIST_INPUT_HARD_LIMITS.commentMaxLength * ratio),
  searchMaxLength: Math.round(CHECKLIST_INPUT_HARD_LIMITS.searchMaxLength * ratio),
  autoSpawnTitleMaxLength: Math.round(CHECKLIST_INPUT_HARD_LIMITS.autoSpawnTitleMaxLength * ratio),
  weightageMax: Math.round(CHECKLIST_INPUT_HARD_LIMITS.weightageMax * ratio),
  estimatedHoursMax: Math.round(CHECKLIST_INPUT_HARD_LIMITS.estimatedHoursMax * ratio),
  actualCostMax: Math.round(CHECKLIST_INPUT_HARD_LIMITS.actualCostMax * ratio),
  countdownHoursMax: Math.round(CHECKLIST_INPUT_HARD_LIMITS.countdownHoursMax * ratio),
  delayHoursMax: Math.round(CHECKLIST_INPUT_HARD_LIMITS.delayHoursMax * ratio),
  autoSpawnYOffsetMax: Math.round(CHECKLIST_INPUT_HARD_LIMITS.autoSpawnYOffsetMax * ratio),
})

export const TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS = getChecklistInputLimitDefaults(0.25)
export const PERSONAL_CHECKLIST_INPUT_LIMITS = getChecklistInputLimitDefaults(0.6)

const clampLimit = (value: unknown, fallback: number, hardMaximum: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.max(1, Math.min(hardMaximum, Math.round(value)))
}

// Effective minimums for the text-content fields. The old ratio (0.25 = 30
// chars for a title) reads as broken on a real workspace — long-form task
// titles regularly need more than 30 chars, and once a user has typed a
// title at exactly that limit the input's HTML maxLength attribute stops
// them from ever growing it back beyond its current length. That's the
// "cannot exceed current length" symptom. Floor these fields at the hard
// cap (their soft-cap becomes the hard cap for team workspaces too, matching
// what personal workspaces already effectively get for practical use). An
// admin can still lower them via the workspace-limits admin UI (soft-cap
// widgets clamp to the hard-cap themselves), but the DEFAULT never
// under-provisions them.
const TEXT_LIMIT_EFFECTIVE_FLOORS = {
  titleMaxLength: CHECKLIST_INPUT_HARD_LIMITS.titleMaxLength,
  descriptionMaxLength: CHECKLIST_INPUT_HARD_LIMITS.descriptionMaxLength,
  completionCriteriaMaxLength: CHECKLIST_INPUT_HARD_LIMITS.completionCriteriaMaxLength,
  commentMaxLength: CHECKLIST_INPUT_HARD_LIMITS.commentMaxLength,
  searchMaxLength: CHECKLIST_INPUT_HARD_LIMITS.searchMaxLength,
  autoSpawnTitleMaxLength: CHECKLIST_INPUT_HARD_LIMITS.autoSpawnTitleMaxLength,
} as const

const clampLimitWithFloor = (value: unknown, fallback: number, hardMaximum: number, effectiveFloor: number) => {
  const clamped = clampLimit(value, fallback, hardMaximum)
  return Math.max(clamped, Math.min(effectiveFloor, hardMaximum))
}

export const normalizeChecklistInputLimits = (
  value: unknown,
  isPersonalWorkspace: boolean,
): ChecklistInputLimits => {
  if (isPersonalWorkspace) return PERSONAL_CHECKLIST_INPUT_LIMITS

  const candidate = value && typeof value === 'object' ? value as Partial<ChecklistInputLimits> : {}
  return {
    titleMaxLength: clampLimitWithFloor(candidate.titleMaxLength, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.titleMaxLength, CHECKLIST_INPUT_HARD_LIMITS.titleMaxLength, TEXT_LIMIT_EFFECTIVE_FLOORS.titleMaxLength),
    descriptionMaxLength: clampLimitWithFloor(candidate.descriptionMaxLength, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.descriptionMaxLength, CHECKLIST_INPUT_HARD_LIMITS.descriptionMaxLength, TEXT_LIMIT_EFFECTIVE_FLOORS.descriptionMaxLength),
    completionCriteriaMaxLength: clampLimitWithFloor(candidate.completionCriteriaMaxLength, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.completionCriteriaMaxLength, CHECKLIST_INPUT_HARD_LIMITS.completionCriteriaMaxLength, TEXT_LIMIT_EFFECTIVE_FLOORS.completionCriteriaMaxLength),
    commentMaxLength: clampLimitWithFloor(candidate.commentMaxLength, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.commentMaxLength, CHECKLIST_INPUT_HARD_LIMITS.commentMaxLength, TEXT_LIMIT_EFFECTIVE_FLOORS.commentMaxLength),
    searchMaxLength: clampLimitWithFloor(candidate.searchMaxLength, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.searchMaxLength, CHECKLIST_INPUT_HARD_LIMITS.searchMaxLength, TEXT_LIMIT_EFFECTIVE_FLOORS.searchMaxLength),
    autoSpawnTitleMaxLength: clampLimitWithFloor(candidate.autoSpawnTitleMaxLength, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.autoSpawnTitleMaxLength, CHECKLIST_INPUT_HARD_LIMITS.autoSpawnTitleMaxLength, TEXT_LIMIT_EFFECTIVE_FLOORS.autoSpawnTitleMaxLength),
    weightageMax: clampLimit(candidate.weightageMax, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.weightageMax, CHECKLIST_INPUT_HARD_LIMITS.weightageMax),
    estimatedHoursMax: clampLimit(candidate.estimatedHoursMax, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.estimatedHoursMax, CHECKLIST_INPUT_HARD_LIMITS.estimatedHoursMax),
    actualCostMax: clampLimit(candidate.actualCostMax, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.actualCostMax, CHECKLIST_INPUT_HARD_LIMITS.actualCostMax),
    countdownHoursMax: clampLimit(candidate.countdownHoursMax, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.countdownHoursMax, CHECKLIST_INPUT_HARD_LIMITS.countdownHoursMax),
    delayHoursMax: clampLimit(candidate.delayHoursMax, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.delayHoursMax, CHECKLIST_INPUT_HARD_LIMITS.delayHoursMax),
    autoSpawnYOffsetMax: clampLimit(candidate.autoSpawnYOffsetMax, TEAM_CHECKLIST_INPUT_DEFAULT_LIMITS.autoSpawnYOffsetMax, CHECKLIST_INPUT_HARD_LIMITS.autoSpawnYOffsetMax),
  }
}

export const clampNumberToLimit = (value: unknown, maximum: number, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value))
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(0, Math.min(maximum, parsed))
}

export const clampIntegerToLimit = (value: unknown, maximum: number, fallback = 0) =>
  Math.round(clampNumberToLimit(value, maximum, fallback))

export const truncateToLimit = (value: string, maximum: number) => value.slice(0, Math.max(0, maximum))
